import { Component, DestroyRef, ElementRef, HostListener, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { GlobalSearchService } from '../../service/global-search.service';
import { GlobalSearchCategory, GlobalSearchTableName } from '../../models/GlobalSearchCategory-model';
import { GlobalSearchRow } from '../../models/GlobalSearchTableResponse-model';

@Component({
    selector: 'app-global-search',
    templateUrl: './global-search.component.html',
    styleUrls: ['./global-search.component.css']
})
export class GlobalSearchComponent {

    private globalSearchService = inject(GlobalSearchService);
    private router = inject(Router);
    private destroyRef = inject(DestroyRef);
    private host = inject(ElementRef<HTMLElement>);

    searchControl = new FormControl('');

    categories = signal<GlobalSearchCategory[]>([]);
    selectedCategory = signal<GlobalSearchCategory | null>(null);
    results = signal<GlobalSearchRow[]>([]);

    currentPage = signal(0);
    totalPages = signal(0);
    totalElements = signal(0);

    isCategoriesLoading = signal(false);
    isResultsLoading = signal(false);
    isOpen = signal(false);

    private readonly pageSize = 10;

    constructor() {
        this.searchControl.valueChanges
            .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
            .subscribe(value => this.onSearchInput(value));
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        if (!this.host.nativeElement.contains(event.target as Node)) {
            this.isOpen.set(false);
        }
    }

    onFocus() {
        if (this.searchControl.value && this.cleanInput(this.searchControl.value).length >= 3) {
            this.isOpen.set(true);
        }
    }

    private cleanInput(input: string | null): string {
        if (!input) return '';
        let cleaned = input.trim();
        if (cleaned.toLowerCase().endsWith('.ols')) {
            cleaned = cleaned.slice(0, -4);
        }
        return cleaned;
    }

    onSearchInput(value: string | null) {
        const cleaned = this.cleanInput(value);
        if (cleaned.length < 3) {
            this.clearResults();
            return;
        }

        this.isOpen.set(true);
        this.selectedCategory.set(null);
        this.results.set([]);
        this.isCategoriesLoading.set(true);

        this.globalSearchService.search(cleaned.toLowerCase())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: res => {
                    const filtered = (res?.categories || []).filter(c => c.tableName !== ('IMPORTS' as GlobalSearchTableName));
                    this.categories.set(filtered);
                    this.isCategoriesLoading.set(false);
                },
                error: () => {
                    this.categories.set([]);
                    this.isCategoriesLoading.set(false);
                }
            });
    }

    selectCategory(category: GlobalSearchCategory) {
        this.selectedCategory.set(category);
        this.currentPage.set(0);
        this.loadResults();
    }

    backToCategories() {
        this.selectedCategory.set(null);
        this.results.set([]);
        this.currentPage.set(0);
        this.totalPages.set(0);
        this.totalElements.set(0);
    }

    nextPage() {
        if (this.currentPage() + 1 >= this.totalPages()) return;
        this.currentPage.update(p => p + 1);
        this.loadResults();
    }

    prevPage() {
        if (this.currentPage() <= 0) return;
        this.currentPage.update(p => p - 1);
        this.loadResults();
    }

    private loadResults() {
        const category = this.selectedCategory();
        if (!category) return;

        this.isResultsLoading.set(true);
        this.globalSearchService.searchInTable(category.tableName, category.searchTerm, this.currentPage(), this.pageSize)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: res => {
                    this.results.set(res?.content || []);
                    this.totalPages.set(res?.totalPages || 0);
                    this.totalElements.set(res?.totalElements || 0);
                    this.isResultsLoading.set(false);
                },
                error: () => {
                    this.results.set([]);
                    this.isResultsLoading.set(false);
                }
            });
    }

    navigate(row: GlobalSearchRow) {
        const category = this.selectedCategory();
        if (!category) return;

        switch (category.tableName) {
            case 'PURE_ORIGINAL_BASE':
                if (row.id == null) return;
                this.router.navigate(['/app/pure-originals/pure-original-details'], {
                    queryParams: { pureOriginalId: row.id }
                });
                break;
            case 'MASTER':
                if (row.id == null) return;
                this.router.navigate(['/app/master-details', row.id]);
                break;
            case 'VERSION':
                if (row.masterId == null) return;
                this.router.navigate([`/app/master-details/${row.masterId}`], {
                    queryParams: { versionName: row.versionName?.trim() }
                });
                break;
            case 'PURE_ORIGINAL_MATCHING':
            case 'PURE_ORIGINAL_MISSING_VERSIONS':
                if (row.pureOriginalBaseId == null) return;
                this.router.navigate(['/app/pure-originals/pure-original-details'], {
                    queryParams: { pureOriginalId: row.pureOriginalBaseId }
                });
                break;
        }

        this.searchControl.setValue('', { emitEvent: false });
        this.clearResults();
    }

    clearResults() {
        this.categories.set([]);
        this.selectedCategory.set(null);
        this.results.set([]);
        this.currentPage.set(0);
        this.totalPages.set(0);
        this.totalElements.set(0);
        this.isCategoriesLoading.set(false);
        this.isResultsLoading.set(false);
        this.isOpen.set(false);
    }

    onClearClick() {
        this.searchControl.setValue('');
        this.clearResults();
    }

    rowPrimary(row: GlobalSearchRow): string {
        const category = this.selectedCategory();
        if (!category) return '';
        switch (category.tableName) {
            case 'PURE_ORIGINAL_BASE':
                return row.winOlsReference || row.winOlsDbName || '—';
            case 'MASTER':
                return row.winOLSReference || '—';
            case 'VERSION':
                return row.versionName || '—';
            case 'PURE_ORIGINAL_MATCHING':
            case 'PURE_ORIGINAL_MISSING_VERSIONS':
                return row.tuningName || '—';
        }
        return '—';
    }

    rowSecondary(row: GlobalSearchRow): string {
        const category = this.selectedCategory();
        if (!category) return '';
        if (category.tableName === 'VERSION' && row.masterWinOLSReference) {
            return row.masterWinOLSReference;
        }
        const parts = [row.vehicleProducer, row.vehicleModel, row.ecuBuild]
            .map(v => v?.toString().trim())
            .filter((v): v is string => !!v);
        return parts.join(' · ');
    }
}
